import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { Cluster } from 'src/app/models/cluster.model';
import { InvestableParameters } from 'src/app/models/investable-parameters.model';
import { Product } from 'src/app/models/product.model';
import { Watchlist } from 'src/app/models/watchlist.model';

import { ClustersService } from 'src/app/services/clusters.service';
import { InvestableService } from 'src/app/services/investable.service';
import { ListsService } from 'src/app/services/lists.service';
import { NotificationsService } from 'src/app/services/notifications.service';

import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";

import { SelectionModel } from '@angular/cdk/collections';
import { MatSort } from '@angular/material/sort';

@Component({
  selector: 'app-investable-constraints',
  templateUrl: './investable-constraints.component.html',
  styleUrls: ['./investable-constraints.component.scss']
})
export class InvestableConstraintsComponent implements OnInit, OnDestroy {

  @Input() optimizeOk!: boolean;
  @Output() optimizeOkChange = new EventEmitter<boolean>();
  @Input() improveOk!: boolean;
  @Output() improveOkChange = new EventEmitter<boolean>();

  @Output() changeBlock = new EventEmitter<string>();

  bestFitSelection: string = 'all';

  selectedPage1: string = 'budget';

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  productList: Product[] = [];

  investableParameters: InvestableParameters;
  investableParametersSubscription: Subscription;

  watchlists: Watchlist[];
  watchlistsSubscription: Subscription;
  selectedWatchlist: Watchlist;

  displayedColumns: string[] = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'factor_intensity'];
  dataSource: MatTableDataSource<Product>;
  selection: SelectionModel<Product>;

  top18FactorIntensity: number;

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  constructor(private clustersService: ClustersService,
              private investableService: InvestableService,
              private listsService: ListsService,
              private notificationsService: NotificationsService,) { }

  ngOnInit(): void {
    this.listsService.loadLists().then(
      () => {
        this.productList = this.listsService.getProductList();
        this.determineTop18FactorIntensity();
        this.dataSource = new MatTableDataSource<Product>(this.productList);
        this.selection = new SelectionModel<Product>(true, []);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = (data: Product, filter: string) => {
          return this.isInBestFitSelection(data, filter) && (
            filter == 'no_filter' ||
            data.currency.alias.trim().toLowerCase() == filter ||
            data.name.trim().toLowerCase().includes(filter) ||
            data.investment_zone.name.trim().toLowerCase().includes(filter)
          );
        }
      }
    );

    this.subscribeToCurrentCluster();
    this.subscribeToInvestableParameters();

    this.subscribeToWatchlists();
  }

  ngOnDestroy() {
    if (this.currentClusterSubscription != undefined) { this.currentClusterSubscription.unsubscribe(); }
    if (this.investableParametersSubscription != undefined) { this.investableParametersSubscription.unsubscribe(); }
  }

  subscribeToCurrentCluster() {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        if (cluster!=undefined) {
          this.currentCluster = cluster;
          if (cluster.is_standalone) {
            this.selectedPage1 = 'bestFit';
          }
        }
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  subscribeToInvestableParameters() {
    this.investableParametersSubscription = this.investableService.investableParametersSubject.subscribe(
      (investableParameters: InvestableParameters) => {
        this.investableParameters = investableParameters;
      }
    );
    this.investableService.emitInvestableParameters();
  }

  subscribeToWatchlists() {
    this.watchlistsSubscription = this.clustersService.watchlistsSubject.subscribe(
      (watchlists: Watchlist[]) => {
        if (watchlists!=undefined && watchlists.length>0) {
          this.selectedWatchlist = watchlists[0];
          this.watchlists = watchlists;
        } else {
          this.watchlists = [];
        }
      }
    );
    this.clustersService.emitWatchlists();
  }

  determineTop18FactorIntensity() {
    this.top18FactorIntensity = this.productList.map((product: Product) => {
      return product.factor_intensity;
    }).sort((a, b) => b - a)[17];
  }

  onChangeBlock(selectBlock: string) {
    this.changeBlock.emit(selectBlock);
  }

  removeConstraints() {
    this.investableParameters.removeConstraints();
    this.onConstraintValueChange();
  }

  lessMoreFivePct() {
    this.investableParameters.fivePercents();
    this.onConstraintValueChange();
  }

  onConstraintValueChange() {
    this.investableParameters.checkWeightConstraints();
  }

  bestFitViewChange(view: string) {
    switch (view) {
      case 'factor-intensity':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'factor_intensity'];
        break;
      case 'returns':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'returns'];
        break;
      case 'sharpe-ratio':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'sharpe_ratio'];
        break;
      case 'max-drawdown':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'maximum_drawdown'];
        break;
      case 'esg':
        this.displayedColumns = ['select', 'frequency', 'name', 'currency', 'investment_zone', 'esg'];
        break;
      default:
        break;
    }
  }

  bestFitSelectionChange() {
    this.dataSource.filter = 'no_filter';
  }

  onProductSelectionChange(event, row) {
    if (event) {
      this.selection.toggle(row)
    }
    this.investableParameters.completeness_products = this.selection.selected;
    // Alert if less than 2 completeness components selected
    if (this.investableParameters.completeness_products.length<2) {
      // TODO: Add possibility to make a specific notification last forever
      this.notificationsService.notify('completeness/too_few_ingredients');
    } else {
      // TODO: Add possibility to hide a specific notification
      this.notificationsService.unnotify('completeness/too_few_ingredients');
    }
    // Reset computation results on components change
    this.setOptimizeOk(false);
    this.setImproveOk(false);
  }

  isInBestFitSelection(product: Product, filter: string) {
    switch (this.bestFitSelection) {
      case 'top18':
        return product.factor_intensity>=this.top18FactorIntensity;
      case 'watchlists':
        return this.selectedWatchlist.products_ids.includes(product.id);
      case 'selected':
        return this.selection.isSelected(product);;
      case 'all':
        return true;
    }
  }

  formatLabel(value: number) {
    if (value < 0.1) {
      return Math.round(10000*value)/100;
    } else {
      return Math.round(1000*value)/10;
    }
  }

  applyFilter(event: Event) {
    let filterValue = (event.target as HTMLInputElement).value;
    if (filterValue=='' || this.bestFitSelection!='all') filterValue = 'no_filter';
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  setOptimizeOk(flag: boolean) {
    if (this.optimizeOk != flag) {
      this.optimizeOk = flag;
      this.optimizeOkChange.emit(flag);
    }
  }

  setImproveOk(flag: boolean) {
    if (this.improveOk != flag) {
      this.improveOk = flag;
      this.improveOkChange.emit(flag);
    }
  }

}
